<template>
  <!-- Dormant newsletter -->
  <div v-if="newsletter.status === 'dormant'" class="card">
    {{ t("web.subscription.message.dormant_notice") }}
  </div>

  <!-- Private newsletter -->
  <div v-else-if="newsletter.should_be_private" class="card">
    {{ t("web.subscription.message.private_notice") }}
  </div>

  <!-- Paid-only gate -->
  <PaywallGateSection
    v-else-if="shouldShowPaywallGate"
    :newsletter="newsletter"
    :email-address="emailAddress"
    :show-free-subscribe-link="canOfferFreeSubscribe"
    @show-free-subscribe="hasChosenFreeSubscribe = true"
  />

  <!-- Standard subscribe form -->
  <div v-else class="card">
    <div class="archive__subscribe">
      <template v-if="showCaptcha">
        <CaptchaChallenge
          :newsletter="newsletter"
          @callback="handleCaptchaToken"
          @error-callback="captchaError = 'failed'"
          @expired-callback="emit('update:turnstileResponseToken', '')"
          @load-error="captchaError = 'unavailable'"
        />
        <p
          v-if="captchaError"
          class="archive__subscribe-captcha-error"
          role="alert"
        >
          {{
            captchaError === "unavailable"
              ? t("web.subscription.error.captcha_unavailable")
              : t("web.subscription.error.captcha_failed")
          }}
        </p>
      </template>
      <form
        v-else
        class="archive__subscribe-form"
        @submit.prevent="handleSubmit"
      >
        <p
          v-if="newsletter.subscription_form_text"
          class="heading archive__subscribe-form-heading"
        >
          {{ newsletter.subscription_form_text }}
        </p>

        <input
          type="submit"
          :value="
            newsletter.subscription_button_text || t('web.button.subscribe')
          "
          class="hidden-submit"
        />

        <div
          v-if="validationAnnouncement"
          role="alert"
          aria-live="assertive"
          class="sr-only"
        >
          {{ validationAnnouncement }}
        </div>

        <!-- Metadata fields -->
        <MetadataFieldsSection
          v-if="showMetadataFields"
          :fields="newsletter.autonomy_fields"
          :metadata="metadata"
          @update:metadata="emit('update:metadata', $event)"
        />

        <!-- Surveys -->
        <SurveyFieldsSection
          v-if="surveys?.length"
          :surveys="surveys"
          :responses="surveyResponses || {}"
          @update:responses="emit('update:surveyResponses', $event)"
        />

        <!-- Email input -->
        <label for="sfi-email" class="archive__subscribe-form-label sr-only">
          {{ t("web.form.placeholder.email_with_example") }}
        </label>
        <input
          v-if="showEmailInput"
          id="sfi-email"
          class="archive__subscribe-form-input archive__input"
          required
          :value="emailAddress"
          type="email"
          :placeholder="t('web.form.placeholder.email_with_example')"
          :readonly="state === 'pending_subscription'"
          autocomplete="email"
          @input="handleEmailInput"
        />

        <!-- RSS-reader nudge (non-blocking second button) -->
        <RssReaderButton v-if="showRssReaderButton" :newsletter="newsletter" />

        <input
          v-if="newsletter.honeypot_field_name"
          :name="newsletter.honeypot_field_name"
          type="text"
          value=""
          tabindex="-1"
          autocomplete="off"
          aria-hidden="true"
          data-lpignore="true"
          data-1p-ignore
          data-bwignore
          style="
            position: absolute;
            left: -9999px;
            width: 1px;
            height: 1px;
            overflow: hidden;
          "
        />

        <!-- Recognized but unauthenticated: prompt magic-link sign-in (B-4406) -->
        <RecognizedSubscriberSignIn
          v-if="recognized"
          :newsletter="newsletter"
          :email-address="emailAddress"
        />

        <!-- State-driven actions and messages -->
        <SubscriptionActionsSection
          v-else
          :newsletter="newsletter"
          :state="state"
          :subscriber="subscriber"
          :email-address="emailAddress"
          :variant="effectiveVariant"
          :error="error"
          :form-disabled="disabled"
          :upgrade-url="upgradeUrl"
          @subscribe="subscribe"
          @resubscribe="resubscribe($event)"
          @check-subscriber="checkSubscriber"
          @highlight-required="highlightRequiredFields"
        >
          <template v-if="sniperLink.data.value" #sniper-link>
            <a
              :href="sniperLink.data.value.url"
              target="_blank"
              rel="noopener noreferrer"
              type="button"
              class="archive__button archive__subscribe-form-button tinted blue sniper-link-button"
            >
              <img
                v-if="sniperLink.data.value.image"
                :src="sniperLink.data.value.image"
                :alt="sniperLink.data.value.provider_pretty"
                class="sniper-link-image"
              />
              <div class="sniper-link-text">
                {{ t("web.action.take_me_to") }}
                {{ sniperLink.data.value.provider_pretty }}
              </div>
            </a>
          </template>
        </SubscriptionActionsSection>
      </form>
    </div>
  </div>

  <!-- Confirmed subscription dialog -->
  <ConfirmedDialog
    v-if="showConfirmedDialog"
    :newsletter="newsletter"
    :upgrade-url="upgradeUrl"
    :archive-url="confirmedDialogArchiveUrl"
    @close="showConfirmedDialog = false"
  />

  <!-- Double opt-in: confirmation required dialog -->
  <ConfirmationRequiredDialog
    v-if="showConfirmationRequiredDialog"
    :newsletter="newsletter"
    :email-address="emailAddress"
    :sniper-link="sniperLink.data.value ?? null"
    @close="showConfirmationRequiredDialog = false"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, toRef, watch } from "vue";

import type { Newsletter } from "@/types/newsletter";

import { createTranslator } from "../lib/utils";
import CaptchaChallenge from "./components/CaptchaChallenge.vue";
import ConfirmationRequiredDialog from "./components/ConfirmationRequiredDialog.vue";
import ConfirmedDialog from "./components/ConfirmedDialog.vue";
import MetadataFieldsSection from "./components/MetadataFieldsSection.vue";
import PaywallGateSection from "./components/PaywallGateSection.vue";
import RecognizedSubscriberSignIn from "./components/RecognizedSubscriberSignIn.vue";
import RssReaderButton from "./components/RssReaderButton.vue";
import SubscriptionActionsSection from "./components/SubscriptionActionsSection.vue";
import SurveyFieldsSection from "./components/SurveyFieldsSection.vue";
import { useSniperLink } from "./composables/useSniperLink";
import {
  type BasicSubscriber,
  type FormSurvey,
  type SubscribeFormVariant,
  type SubscriptionState,
  isRssReaderEmail,
  isValidEmail,
  mutableUrl,
  publicArchiveUrl,
} from "./lib";

const props = defineProps<{
  newsletter: Newsletter;
  subscriber?: BasicSubscriber;
  state: SubscriptionState;
  variant: SubscribeFormVariant;
  emailAddress: string;
  metadata: Record<string, string>;
  turnstileResponseToken: string;
  error?: string;
  recognized?: boolean;
  surveys?: FormSurvey[];
  surveyResponses?: Record<string, string | number | number[]>;
  subscribe: () => void;
  resubscribe: (subscriberId: string) => void;
  checkSubscriber: () => void;
}>();

const emit = defineEmits<{
  "update:emailAddress": [value: string];
  "update:metadata": [value: Record<string, string>];
  "update:turnstileResponseToken": [value: string];
  "update:surveyResponses": [value: Record<string, string | number | number[]>];
}>();

const t = createTranslator(() => props.newsletter.locale);

const showConfirmedDialog = ref(false);
const showConfirmationRequiredDialog = ref(false);
const validationAnnouncement = ref("");
const captchaError = ref<"failed" | "unavailable" | null>(null);

function handleCaptchaToken(token: string) {
  captchaError.value = null;
  emit("update:turnstileResponseToken", token);
}

const disabled = computed(() => {
  return (
    !props.emailAddress ||
    !isValidEmail(props.emailAddress) ||
    (props.newsletter.autonomy_fields || [])
      .filter((field) => "required" in field.config && field.config.required)
      .some((field) => !props.metadata[field.key])
  );
});

const upgradeUrl = computed(() => {
  return mutableUrl(props.newsletter, props.subscriber, "/upgrade/monthly");
});

const confirmedDialogArchiveUrl = computed(() => {
  return publicArchiveUrl(props.newsletter);
});

const sniperLink = useSniperLink(
  toRef(props, "emailAddress"),
  props.newsletter,
  toRef(props, "state")
);

const isPaidOnly = computed(() => {
  return (
    (props.newsletter.should_disable_non_premium_subscriptions ||
      props.variant === "paywalled_archive_page") &&
    !props.subscriber &&
    props.newsletter.paid_subscriptions_status === "active"
  );
});

const hasChosenFreeSubscribe = ref(false);

const canOfferFreeSubscribe = computed(() => {
  return (
    props.variant === "paywalled_archive_page" &&
    !props.newsletter.should_disable_non_premium_subscriptions
  );
});

const shouldShowPaywallGate = computed(() => {
  return isPaidOnly.value && !hasChosenFreeSubscribe.value;
});

// SubscriptionActionsSection swaps the submit button to "Log in" when variant
// is `paywalled_archive_page` — once the visitor opts into free subscribe,
// behave as a normal archive subscribe instead.
const effectiveVariant = computed<SubscribeFormVariant>(() => {
  if (
    hasChosenFreeSubscribe.value &&
    props.variant === "paywalled_archive_page"
  ) {
    return "archive_page";
  }
  return props.variant;
});

const showMetadataFields = computed(() => {
  return (
    props.state !== "pending_subscription" &&
    props.state !== "unactivated" &&
    !props.subscriber
  );
});

const showEmailInput = computed(() => {
  return (
    props.state !== "unactivated" &&
    !props.subscriber &&
    props.state !== "subscribed_elsewhere"
  );
});

const showRssReaderButton = computed(() => {
  return showEmailInput.value && isRssReaderEmail(props.emailAddress);
});

const showCaptcha = computed(() => {
  if (props.turnstileResponseToken) return false;
  return (
    props.state === "errored_email" || props.state === "invalid_subscription"
  );
});

function handleEmailInput(event: Event) {
  const el = event.target as HTMLInputElement;
  emit("update:emailAddress", el.value);
}

function highlightRequiredFields() {
  if (!disabled.value) return;

  const requiredFields = props.newsletter.autonomy_fields
    .filter((field) => "required" in field.config && field.config.required)
    .filter((field) => !props.metadata[field.key]);

  const fieldNames = requiredFields.map((f) => f.label).join(", ");
  validationAnnouncement.value = t(
    "web.form.validation.required_fields"
  ).replace("{fields}", fieldNames);

  requiredFields.forEach((field) => {
    const element = document.getElementById(`sfi-${field.key}`);
    if (element) {
      element.classList.add("required");
      element.setAttribute("aria-invalid", "true");
    }
  });

  setTimeout(() => {
    validationAnnouncement.value = "";
    requiredFields.forEach((field) => {
      const element = document.getElementById(`sfi-${field.key}`);
      if (element) {
        element.classList.remove("required");
        element.removeAttribute("aria-invalid");
      }
    });
  }, 2000);
}

function handleSubmit() {
  if (!disabled.value) {
    props.subscribe();
  }
}

// A fresh client-side subscribe only swaps the button for a line of
// easy-to-miss text (e.g. subscribing from the archive page) — the celebratory
// ConfirmedDialog and the double-opt-in reminder both used to fire only on the
// legacy full-page redirect (`?state=confirmed_subscription`), never here. Pop
// the matching modal when a subscribe settles, so the outcome is unmistakable.
// Gating on the `pending_subscription` → settled transition keeps it from firing
// on page load for someone who is already subscribed/unactivated.
watch(
  () => props.state,
  (state, previousState) => {
    if (previousState !== "pending_subscription") return;
    if (state === "unactivated") {
      showConfirmationRequiredDialog.value = true;
    } else if (state === "regular") {
      showConfirmedDialog.value = true;
    }
  }
);

onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get("state") === "confirmed_subscription") {
    showConfirmedDialog.value = true;
    urlParams.delete("state");
    const newUrl =
      window.location.pathname +
      (urlParams.toString() ? `?${urlParams.toString()}` : "") +
      window.location.hash;
    window.history.replaceState({}, "", newUrl);
  }
});
</script>
